import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { authClient } from '../services/authClient';
import { useAuth } from '../context/AuthContext';
import useEscapeToClose from '../hooks/useEscapeToClose';
import colors from '../theme/colors';
import { IconMail, IconLock } from './icons';

// ── Styled components ────────────────────────────────────────────────────────
const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20000;
  padding: 20px;
`;

const Modal = styled(motion.div)`
  /* Same treatment as the other modals: translucent theme blue + white streaks */
  background-color: ${colors.base.themePrimaryTransparent};
  background-image:
    linear-gradient(73deg,  transparent calc(47% - 1px), rgba(255,255,255,0.14) 47%, transparent calc(47% + 1px)),
    linear-gradient(161deg, transparent calc(38% - 1px), rgba(255,255,255,0.10) 38%, transparent calc(38% + 1px)),
    linear-gradient(210deg, transparent calc(55% - 1px), rgba(255,255,255,0.08) 55%, transparent calc(55% + 1px)),
    linear-gradient(30deg,  transparent calc(68% - 1px), rgba(255,255,255,0.06) 68%, transparent calc(68% + 1px));
  border: 1px solid ${colors.border.light};
  border-radius: 16px;
  padding: 32px 28px;
  width: 100%;
  max-width: 400px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;

  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-track { background: ${colors.ui.scrollbarTrackDark}; border-radius: 4px; }
  &::-webkit-scrollbar-thumb { background: ${colors.ui.scrollbarThumb}; border-radius: 4px; }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid ${colors.border.light};
  border-radius: 50%;
  width: 32px;
  height: 32px;
  color: white;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover { background: rgba(255, 255, 255, 0.2); transform: rotate(90deg); }
`;

const Title = styled.h2`
  color: ${colors.accent.primary};
  font-size: 1.3rem;
  font-weight: 700;
  margin: 0 0 20px 0;
  text-align: center;
`;

const SocialButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 11px 16px;
  margin-bottom: 10px;
  background: ${colors.base.themePrimary};
  border: 1px solid ${colors.border.light};
  border-radius: 24px;
  color: ${colors.text.light};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.button.aboutBgHover};
    border-color: ${colors.border.lightHover};
    transform: translateY(-1px);
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 18px 0;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.75rem;

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.2);
  }
`;

const Field = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 14px;
  margin-bottom: 10px;
  border-radius: 8px;
  border: 1px solid ${colors.border.light};
  background: rgba(255, 255, 255, 0.08);
  color: white;
  font-size: 14px;

  &::placeholder { color: rgba(255, 255, 255, 0.4); }

  &:focus {
    outline: none;
    border-color: ${colors.accent.primary};
    background: rgba(255, 255, 255, 0.12);
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 16px;
  margin-top: 4px;
  background: ${colors.base.themePrimary};
  color: ${colors.accent.primary};
  border: 1px solid ${colors.accent.secondary};
  border-radius: 24px;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 0 14px rgba(255, 255, 255, 0.35);
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.5);
  }

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const LinkRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 14px;
  font-size: 0.78rem;
`;

const TextLink = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.65);
  cursor: pointer;
  padding: 0;
  font-size: inherit;
  text-decoration: underline;

  &:hover { color: white; }
`;

const Message = styled.div`
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 0.8rem;
  line-height: 1.4;
  background: ${props => props.$error ? 'rgba(220, 60, 60, 0.18)' : 'rgba(60, 180, 100, 0.18)'};
  border: 1px solid ${props => props.$error ? 'rgba(220, 60, 60, 0.45)' : 'rgba(60, 180, 100, 0.45)'};
  color: white;
`;

// ── Provider icons ───────────────────────────────────────────────────────────
const GoogleIcon = () => (
  <svg width="17" height="17" viewBox="0 0 18 18">
    <path fill="#4285F4" d="M9 3.6c1.4 0 2.6.5 3.6 1.3l2.6-2.6C13.8 1.1 11.6 0 9 0 5.5 0 2.5 2.2 1.3 5.3l3.1 2.4c.7-2.3 2.9-3.9 5.3-3.9z"/>
    <path fill="#34A853" d="M1.3 12.7c-.3-.8-.3-1.8-.3-2.7s0-1.9.3-2.7L1.3 5.3C.5 6.7 0 8.3 0 10s.5 3.3 1.3 4.7l3.1-2.4z"/>
    <path fill="#FBBC04" d="M9 18c2.6 0 4.8-1.1 6.2-2.3l-3.1-2.4c-.9.6-2 1-3.1 1-2.4 0-4.5-1.6-5.3-3.9l-3.1 2.4C2.5 15.8 5.5 18 9 18z"/>
    <path fill="#EA4335" d="M18 10c0-.8-.1-1.6-.3-2.3H9v4.6h5.1c-.2 1.3-1 2.4-2 3.1l3.1 2.4C16.8 16.2 18 13.4 18 10z"/>
  </svg>
);

const XIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const MicrosoftIcon = () => (
  <svg width="15" height="15" viewBox="0 0 23 23">
    <rect x="1" y="1" width="10" height="10" fill="#F25022"/>
    <rect x="12" y="1" width="10" height="10" fill="#7FBA00"/>
    <rect x="1" y="12" width="10" height="10" fill="#00A4EF"/>
    <rect x="12" y="12" width="10" height="10" fill="#FFB900"/>
  </svg>
);

// ── Component ────────────────────────────────────────────────────────────────
/* A password-reset email lands on /?reset=1&token=... Desktop and mobile each render
   their own <AuthButton>, so two LoginModal portals exist at once. The token is therefore
   claimed exactly once, at module load — before any render — and the first instance to
   mount takes it and opens itself; the second gets null and stays shut. Reading it in a
   per-instance effect instead meant one modal consumed the token and cleared the URL while
   the other opened an empty sign-in dialog on top of it. */
const pendingResetToken = (() => {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  if (params.get('reset') !== '1' || !token) return null;
  window.history.replaceState({}, document.title, window.location.pathname);
  return token;
})();

// Which instance owns the reset flow. Never cleared: StrictMode runs effects twice, so
// the owner must be able to re-run its own claim without the token having been consumed.
let resetOwner = null;

// Modes: signin | signup | forgot | reset
const LoginModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { refreshUser } = useAuth();
  const [mode, setMode] = useState('signin');
  // The reset link has to open the modal on its own — nothing else does
  const [selfOpen, setSelfOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null); // { text, error }
  const [resetToken, setResetToken] = useState(null);
  const instanceRef = useRef({});
  // Only show buttons for providers whose secrets are actually configured
  const [providers, setProviders] = useState({ google: true });

  const visible = isOpen || selfOpen;
  const handleClose = () => {
    setSelfOpen(false);
    onClose();
  };

  useEscapeToClose(visible, handleClose);

  // Take the reset token if no sibling instance has already taken it. Re-entrant on
  // purpose: the owner may run this twice under StrictMode and must reach the same state.
  useEffect(() => {
    if (!pendingResetToken) return;
    if (resetOwner && resetOwner !== instanceRef.current) return;
    resetOwner = instanceRef.current;
    setResetToken(pendingResetToken);
    setMode('reset');
    setSelfOpen(true);
  }, []);

  useEffect(() => {
    if (!visible) return;
    const base = window.location.port === '3000' ? 'http://localhost:8787' : '';
    fetch(`${base}/api/config/providers`)
      .then(r => r.json())
      .then(data => { if (data?.providers) setProviders(data.providers); })
      .catch(() => {}); // keep defaults on failure
  }, [visible]);

  const callbackURL = `${window.location.origin}/?login=success`;

  const handleSocial = async (provider) => {
    setMessage(null);
    setBusy(true);
    const { error } = await authClient.signIn.social({ provider, callbackURL });
    if (error) {
      setMessage({ text: error.message || t('auth.socialError'), error: true });
      setBusy(false);
    }
    // On success the browser navigates away — nothing else to do
  };

  const finishLogin = async () => {
    await refreshUser();
    setSelfOpen(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setBusy(true);
    try {
      if (mode === 'signin') {
        const { error } = await authClient.signIn.email({ email, password });
        if (error) {
          // 403 = valid credentials but email not verified yet
          const text = error.status === 403 ? t('auth.emailNotVerified') : t('auth.invalidCredentials');
          setMessage({ text, error: true });
        } else await finishLogin();
      } else if (mode === 'signup') {
        const { error } = await authClient.signUp.email({ email, password, name: name || email.split('@')[0] });
        // Generic message — never echo the backend error, which can confirm
        // whether an email address is registered (user enumeration)
        if (error) setMessage({ text: t('auth.signupError'), error: true });
        else {
          // No session until the email is verified — send them to their inbox
          setMessage({ text: t('auth.verificationSent'), error: false });
          setMode('signin');
          setPassword('');
        }
      } else if (mode === 'forgot') {
        const { error } = await authClient.requestPasswordReset({
          email,
          redirectTo: `${window.location.origin}/?reset=1`,
        });
        if (error) setMessage({ text: error.message || t('auth.resetError'), error: true });
        else setMessage({ text: t('auth.resetSent'), error: false });
      } else if (mode === 'reset') {
        const { error } = await authClient.resetPassword({ newPassword: password, token: resetToken });
        if (error) setMessage({ text: error.message || t('auth.resetError'), error: true });
        else {
          setMessage({ text: t('auth.passwordChanged'), error: false });
          setMode('signin');
          setPassword('');
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const titles = {
    signin: t('auth.signInTitle'),
    signup: t('auth.signUpTitle'),
    forgot: t('auth.forgotTitle'),
    reset: t('auth.resetTitle'),
  };

  if (!visible) return null;

  return createPortal(
    <AnimatePresence>
      <Overlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleClose}
      >
        <Modal
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.85, opacity: 0 }}
          transition={{ type: 'spring', damping: 25 }}
          onClick={(e) => e.stopPropagation()}
        >
          <CloseButton onClick={handleClose}>×</CloseButton>
          <Title>{titles[mode]}</Title>

          {message && <Message $error={message.error}>{message.text}</Message>}

          {(mode === 'signin' || mode === 'signup') && (
            <>
              {providers.google && (
                <SocialButton onClick={() => handleSocial('google')} disabled={busy}>
                  <GoogleIcon /> {t('auth.continueWithGoogle')}
                </SocialButton>
              )}
              {providers.twitter && (
                <SocialButton onClick={() => handleSocial('twitter')} disabled={busy}>
                  <XIcon /> {t('auth.continueWithX')}
                </SocialButton>
              )}
              {providers.microsoft && (
                <SocialButton onClick={() => handleSocial('microsoft')} disabled={busy}>
                  <MicrosoftIcon /> {t('auth.continueWithMicrosoft')}
                </SocialButton>
              )}

              <Divider>{t('auth.orWithEmail')}</Divider>
            </>
          )}

          <form onSubmit={handleSubmit}>
            {mode === 'signup' && (
              <Field
                type="text"
                placeholder={t('common.yourName')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
              />
            )}
            {mode !== 'reset' && (
              <Field
                type="email"
                placeholder={t('common.yourEmail')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            )}
            {mode !== 'forgot' && (
              <Field
                type="password"
                placeholder={mode === 'reset' ? t('auth.newPassword') : t('auth.password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
              />
            )}

            <SubmitButton type="submit" disabled={busy}>
              {mode === 'forgot' ? <IconMail size={15} /> : <IconLock size={15} />}
              {mode === 'signin' && t('auth.signIn')}
              {mode === 'signup' && t('auth.signUp')}
              {mode === 'forgot' && t('auth.sendResetLink')}
              {mode === 'reset' && t('auth.changePassword')}
            </SubmitButton>
          </form>

          <LinkRow>
            {mode === 'signin' && (
              <>
                <TextLink onClick={() => { setMode('signup'); setMessage(null); }}>
                  {t('auth.noAccountYet')}
                </TextLink>
                <TextLink onClick={() => { setMode('forgot'); setMessage(null); }}>
                  {t('auth.forgotPassword')}
                </TextLink>
              </>
            )}
            {mode !== 'signin' && (
              <TextLink onClick={() => { setMode('signin'); setMessage(null); }}>
                {t('auth.backToSignIn')}
              </TextLink>
            )}
          </LinkRow>
        </Modal>
      </Overlay>
    </AnimatePresence>,
    document.body
  );
};

export default LoginModal;
